import { useEffect, useState } from 'react';
import { basket, useBasketMeta } from '../utils/basketStore';

// שיתוף רשימת הקניות עם בני הבית (BasketTab): קישור הזמנה <site>/?joinList=<shareCode> (spShareShoppingList).
// מי שפותח את הקישור ומתחבר מצטרף לאותה רשימה (JoinListDialog) — כולם רואים ועורכים את אותה רשימה.

export const joinLink = (code) => `${window.location.origin}/?joinList=${code}`;

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '8vh 12px', zIndex: 120, overflowY: 'auto' },
  dialog:  { width: '100%', maxWidth: 520, background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 16px 48px rgba(0,0,0,.25)' },
  header:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 },
  title:   { margin: 0, fontSize: 20, fontWeight: 700 },
  close:   { background: 'none', border: 'none', fontSize: 26, lineHeight: 1, color: '#59636e' },
  text:    { margin: '0 0 14px', color: '#59636e', fontSize: 14, lineHeight: 1.6 },
  linkRow: { display: 'flex', gap: 8, marginBottom: 12 },
  link:    { flex: 1, minWidth: 0, padding: '10px 12px', fontSize: 14, border: '1px solid #d0d7de', borderRadius: 10, background: '#f6f8fa', direction: 'ltr' },
  copy:    { padding: '10px 14px', fontSize: 14, fontWeight: 600, border: '1px solid #1a3f9c', borderRadius: 10, background: '#fff', color: '#1a3f9c', whiteSpace: 'nowrap' },
  whatsapp:{ display: 'block', width: '100%', padding: '12px 16px', fontSize: 16, fontWeight: 700, border: 'none', borderRadius: 10, background: '#25d366', color: '#fff', textAlign: 'center', textDecoration: 'none' },
  members: { marginTop: 18, paddingTop: 14, borderTop: '1px solid #eee' },
  subhead: { margin: '0 0 8px', fontSize: 14, fontWeight: 700 },
  member:  { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 14 },
  avatar:  { width: 28, height: 28, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#1a3f9c', color: '#fff', fontSize: 12, fontWeight: 700 },
  tag:     { fontSize: 12, color: '#59636e' },
  revoke:  { marginTop: 12, background: 'none', border: 'none', padding: 0, color: '#cf222e', fontSize: 13, fontWeight: 600 },
  error:   { background: '#fff1f0', border: '1px solid #ffccc7', color: '#a8071a', padding: '10px 14px', borderRadius: 10, marginBottom: 12 },
  note:    { fontSize: 13, color: '#1a7f37', marginBottom: 10 },
};

const ShareListDialog = ({ onClose }) => {
  const { list, members } = useBasketMeta();
  const [code, setCode] = useState(list?.shareCode || null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  // הקישור נוצר בפעם הראשונה שפותחים את החלון
  useEffect(() => {
    if (code) return;
    basket.share().then((r) => (r.ok ? setCode(r.shareCode) : setError(r.msg)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const link = code ? joinLink(code) : '';
  const inviteText = `הצטרפו לרשימת הקניות המשותפת שלנו ב"זול בכפר" — כל מה שמוסיפים מופיע אצל כולם, ורואים איפה הכי זול 🛒\n${link}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setNote('הקישור הועתק');
    } catch (e) {
      setNote('לא הצלחנו להעתיק — אפשר לסמן את הקישור ולהעתיק ידנית');
    }
    setTimeout(() => setNote(null), 3000);
  };

  const revoke = async () => {
    if (!window.confirm('לבטל את הקישור הנוכחי וליצור חדש? מי שכבר הצטרף נשאר ברשימה.')) return;
    const r = await basket.share(true);
    if (r.ok) { setCode(r.shareCode); setNote('נוצר קישור חדש. הקישור הקודם כבר לא עובד'); } else setError(r.msg);
  };

  return (
    <div style={s.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.dialog} role="dialog" aria-modal="true" aria-labelledby="share-title">
        <div style={s.header}>
          <h2 id="share-title" style={s.title}>👨‍👩‍👧 רשימה משותפת לבני הבית</h2>
          <button type="button" style={s.close} onClick={onClose} aria-label="סגירה">×</button>
        </div>
        <p style={s.text}>
          שלחו את הקישור לבני הבית. מי שנכנס דרכו (עם משתמש באתר) מצטרף לרשימה הזו — כל מה שמוסיפים או מסמנים מופיע אצל כולם.
        </p>

        {error && <div style={s.error} role="alert">{error}</div>}
        {note && <div style={s.note} role="status">{note}</div>}

        {!code && !error && <p style={s.text}>יוצר קישור…</p>}
        {code && (
          <>
            <div style={s.linkRow}>
              <input style={s.link} value={link} readOnly onFocus={(e) => e.target.select()} aria-label="קישור הזמנה" />
              <button type="button" style={s.copy} onClick={copy}>העתקה</button>
            </div>
            <a style={s.whatsapp} href={`https://wa.me/?text=${encodeURIComponent(inviteText)}`} target="_blank" rel="noopener noreferrer">
              שליחת הזמנה בוואטסאפ
            </a>
          </>
        )}

        {members.length > 0 && (
          <div style={s.members}>
            <h3 style={s.subhead}>ברשימה ({members.length})</h3>
            {members.map((m) => (
              <div key={m.userId} style={s.member}>
                <span style={s.avatar} aria-hidden="true">{m.firstName?.[0]}{m.lastName?.[0]}</span>
                {m.firstName} {m.lastName}
                {m.isMe && <span style={s.tag}>(את/ה)</span>}
                {m.isOwner && <span style={s.tag}>· שיתף/ה</span>}
              </div>
            ))}
            {list?.isOwner && code && (
              <button type="button" style={s.revoke} onClick={revoke}>ביטול הקישור ויצירת קישור חדש</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareListDialog;
